import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
    FlexDiv,
    Heading2,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    StatusBadge,
    Tag,
    palette,
} from '@awarego-ui/components';
import { Alert, LinearProgress } from '@mui/material';

const SignalCard = styled.div`
    position: relative;
    padding: 6px 10px;
    border-radius: 4px;
    background-color: ${(p) => {
        // Pinned colour wins when both flags are set, so the user can still
        // see the gold accent even after they've also marked the signal as
        // "Not me" — the pin is the persistent, ground-truth state.
        if (p.$pinned) return palette.pastel.yellow;
        if (p.$dismissed) return palette.graphite.cloud;
        return palette.evergreen.mintLight;
    }};
    border: 1px solid
        ${(p) => {
            if (p.$pinned) return palette.vibrant.gold;
            if (p.$dismissed) return palette.graphite.gray;
            return palette.evergreen.mint;
        }};
    opacity: ${(p) => (p.$dismissed && !p.$pinned ? 0.55 : 1)};
    max-width: 100%;

    & .signal-actions {
        position: absolute;
        right: 6px;
        bottom: 6px;
        display: flex;
        gap: 4px;
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    &:hover .signal-actions,
    & .signal-actions.has-active {
        opacity: 1;
    }

    & .signal-action {
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        line-height: 14px;
        font-weight: 600;
        background: ${palette.graphite.white};
        color: ${palette.graphite.heavyMetal};
        border: 1px solid ${palette.graphite.ash};
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease,
            border-color 0.15s ease;
    }

    & .signal-action:hover:not(:disabled) {
        background: ${palette.graphite.heavyMetal};
        color: ${palette.graphite.white};
        border-color: ${palette.graphite.heavyMetal};
    }

    & .signal-action.pin-active {
        background: ${palette.vibrant.gold};
        color: ${palette.graphite.white};
        border-color: ${palette.vibrant.gold};
    }
    & .signal-action.pin-active:hover:not(:disabled) {
        background: ${palette.graphite.charcoal};
        border-color: ${palette.graphite.charcoal};
    }

    & .signal-action.dismiss-active {
        background: ${palette.evergreen.aspargus};
        color: ${palette.graphite.white};
        border-color: ${palette.evergreen.aspargus};
    }

    & .signal-action.notme-hover:hover:not(:disabled) {
        background: ${palette.vibrant.red};
        color: ${palette.graphite.white};
        border-color: ${palette.vibrant.red};
    }

    & .signal-action:disabled {
        cursor: wait;
        opacity: 0.6;
    }

    & .signal-pin-badge {
        display: inline-block;
        margin-right: 6px;
        padding: 0 6px;
        border-radius: 8px;
        font-size: 10px;
        line-height: 14px;
        font-weight: 700;
        background: ${palette.vibrant.gold};
        color: ${palette.graphite.white};
        text-transform: uppercase;
        letter-spacing: 0.5px;
        vertical-align: 1px;
    }

    & .signal-text {
        text-decoration: ${(p) => (p.$dismissed ? 'line-through' : 'none')};
        padding-right: ${(p) =>
            p.$dismissed || p.$pinned ? '110px' : '8px'};
        display: inline-block;
    }
`;

const LEVEL_COLOR = {
    Critical: palette.vibrant.raspberry,
    High: palette.vibrant.red,
    Medium: palette.vibrant.gold,
    Low: palette.evergreen.aspargus,
};

function ExposureBar({ score }) {
    const value = Math.max(0, Math.min(100, Number(score) || 0));
    const barColor =
        value > 75
            ? LEVEL_COLOR.Critical
            : value > 50
              ? LEVEL_COLOR.High
              : value > 25
                ? LEVEL_COLOR.Medium
                : LEVEL_COLOR.Low;
    return (
        <FlexDiv column gap={4} style={{ width: '100%' }}>
            <FlexDiv style={{ justifyContent: 'space-between' }}>
                <TextTiny color={palette.graphite.charcoal}>
                    Exposure score
                </TextTiny>
                <TextTiny color={palette.graphite.heavyMetal}>
                    <strong>{value}</strong> / 100
                </TextTiny>
            </FlexDiv>
            <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: palette.graphite.gray,
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: barColor,
                    },
                }}
            />
        </FlexDiv>
    );
}

function signalKey(s, i) {
    if (typeof s === 'string') return `s_${i}_${s.slice(0, 60)}`;
    if (!s || typeof s !== 'object') return `s_${i}`;
    const text =
        s.text || s.finding || s.detail || s.description || s.value || '';
    return `s_${i}_${(s.source || '').slice(0, 20)}_${text.slice(0, 60)}`;
}

function signalToText(s) {
    if (typeof s === 'string') return s;
    if (!s || typeof s !== 'object') return '';
    const detail =
        s.text || s.finding || s.detail || s.description || s.value || '';
    return s.source ? `${s.source}: ${detail}` : detail;
}

export default function AwareExposeProfile({
    person,
    profile,
    loadingAttacks,
    onGenerateAttacks,
    onRequalify,
    onTogglePin,
    pinningKeys,
    error,
    warnings,
}) {
    const signals = Array.isArray(profile.signals) ? profile.signals : [];
    const breakdown = Array.isArray(profile.exposure_breakdown)
        ? profile.exposure_breakdown
        : [];
    const personFirst =
        (person && person.name && person.name.split(' ')[0]) || 'Your';
    const levelColor =
        LEVEL_COLOR[profile.exposure_level] || LEVEL_COLOR.Medium;

    const [dismissed, setDismissed] = useState(() => new Set());
    const dismissedTexts = useMemo(() => {
        const out = [];
        signals.forEach((s, i) => {
            // Pinned signals are user-confirmed ground truth — they can never
            // be sent as "excluded" to the LLM even if the local dismiss flag
            // was set before pinning.
            const isPinned = typeof s === 'object' && s ? !!s.pinned : false;
            if (isPinned) return;
            const k = signalKey(s, i);
            if (dismissed.has(k)) {
                const t = signalToText(s);
                if (t) out.push(t);
            }
        });
        return out;
    }, [signals, dismissed]);
    const canRequalify = !!onRequalify && dismissedTexts.length > 0;

    const toggleDismiss = (k) => {
        setDismissed((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
    };

    const handleRequalify = () => {
        if (!canRequalify) return;
        onRequalify(dismissedTexts);
    };

    return (
        <div style={{ width: '100%' }}>
            <FlexDiv column gap={6} mb={24}>
                <TextTiny
                    color={palette.evergreen.aspargus}
                    style={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        display: 'block',
                    }}
                >
                    Step 1 of 2 — Your exposure
                </TextTiny>
                <Heading2 displayBlock>
                    {personFirst}&rsquo;s exposure profile
                </Heading2>
                <TextDefault color={palette.graphite.charcoal}>
                    Here is what an attacker finds about{' '}
                    {(person && person.name) || 'you'} using only public
                    sources. Your exposure score determines a large part of
                    your AwareExpose risk profile.
                </TextDefault>
            </FlexDiv>

            <FlexDiv
                alignCenter
                gap={16}
                padding={20}
                borderRadius={12}
                mb={24}
                backgroundColor={palette.pastel.red}
                style={{ border: `1px solid ${palette.pastel.red}` }}
            >
                <ExposureBar score={profile.exposure_score} />
                <StatusBadge
                    text={profile.exposure_level || 'Medium'}
                    color={levelColor}
                />
            </FlexDiv>

            <FlexDiv
                column
                gap={12}
                padding={20}
                borderRadius={12}
                mb={24}
                backgroundColor={palette.graphite.white}
                style={{ border: `1px solid ${palette.graphite.gray}` }}
            >
                <Heading3 displayBlock>
                    {profile.signals_count || signals.length} public signals
                    found
                </Heading3>
                {(onRequalify || onTogglePin) && (
                    <TextTiny
                        color={palette.graphite.charcoal}
                        style={{ display: 'block' }}
                    >
                        Hover any signal to{' '}
                        {onTogglePin && (
                            <strong>pin it as confirmed</strong>
                        )}
                        {onTogglePin && onRequalify ? ' or ' : ''}
                        {onRequalify && (
                            <strong>mark it as not about you</strong>
                        )}
                        . Pinned signals are kept across re-runs;
                        {onRequalify
                            ? ' re-qualify to drop excluded ones.'
                            : ' nothing else gets stored.'}
                    </TextTiny>
                )}
                <FlexDiv style={{ flexWrap: 'wrap', gap: 8 }}>
                    {signals.map((s, i) => {
                        const detail =
                            typeof s === 'string'
                                ? s
                                : s &&
                                  (s.text ||
                                      s.finding ||
                                      s.detail ||
                                      s.description ||
                                      s.value ||
                                      '');
                        const source = typeof s === 'object' && s ? s.source : null;
                        const text = source ? `${source}: ${detail}` : detail;
                        if (!detail) return null;
                        const k = signalKey(s, i);
                        const isDismissed = dismissed.has(k);
                        const isPinned =
                            typeof s === 'object' && s ? !!s.pinned : false;
                        const isPinning =
                            pinningKeys && pinningKeys.has && pinningKeys.has(k);
                        const showActions = !!onRequalify || !!onTogglePin;
                        const hasActiveBadge = isPinned || isDismissed;
                        return (
                            <SignalCard
                                key={k}
                                $dismissed={isDismissed}
                                $pinned={isPinned}
                            >
                                <TextTiny
                                    className="signal-text"
                                    color={palette.graphite.heavyMetal}
                                >
                                    {isPinned && (
                                        <span
                                            className="signal-pin-badge"
                                            title="You confirmed this signal — it will be passed to the LLM on every re-run."
                                        >
                                            Pinned
                                        </span>
                                    )}
                                    {text}
                                </TextTiny>
                                {showActions && (
                                    <div
                                        className={`signal-actions${
                                            hasActiveBadge ? ' has-active' : ''
                                        }`}
                                    >
                                        {onTogglePin && (
                                            <button
                                                type="button"
                                                className={`signal-action${
                                                    isPinned ? ' pin-active' : ''
                                                }`}
                                                disabled={isPinning}
                                                onClick={() =>
                                                    onTogglePin(s, k, i)
                                                }
                                                aria-pressed={isPinned}
                                                title={
                                                    isPinned
                                                        ? 'Unpin this signal'
                                                        : 'Pin: confirm this signal and keep it across re-runs'
                                                }
                                            >
                                                {isPinning
                                                    ? '…'
                                                    : isPinned
                                                      ? 'Pinned'
                                                      : 'Pin'}
                                            </button>
                                        )}
                                        {onRequalify && (
                                            <button
                                                type="button"
                                                className={`signal-action notme-hover${
                                                    isDismissed
                                                        ? ' dismiss-active'
                                                        : ''
                                                }`}
                                                onClick={() => toggleDismiss(k)}
                                                aria-pressed={isDismissed}
                                                title={
                                                    isDismissed
                                                        ? 'Click to re-include this signal'
                                                        : 'Mark this signal as not about me'
                                                }
                                            >
                                                {isDismissed
                                                    ? 'Excluded'
                                                    : 'Not me'}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </SignalCard>
                        );
                    })}
                </FlexDiv>
                {canRequalify && (
                    <FlexDiv
                        alignCenter
                        gap={12}
                        mt={12}
                        style={{ flexWrap: 'wrap' }}
                    >
                        <ThemedButton
                            mode="secondary"
                            size="small"
                            $noTextTransform
                            text={`Re-qualify (${dismissedTexts.length} excluded)`}
                            onClick={handleRequalify}
                        />
                        <TextTiny color={palette.graphite.charcoal}>
                            Re-runs the scan and tells the model to ignore the
                            signals you marked as &ldquo;not me&rdquo;.
                        </TextTiny>
                    </FlexDiv>
                )}
            </FlexDiv>

            {breakdown.length > 0 && (
                <FlexDiv
                    column
                    gap={4}
                    padding={20}
                    borderRadius={12}
                    mb={24}
                    backgroundColor={palette.graphite.white}
                    style={{
                        border: `1px dashed ${palette.evergreen.mint}`,
                    }}
                >
                    <Heading3 displayBlock>Exposure score breakdown</Heading3>
                    {breakdown.map((b, i) => (
                        <FlexDiv
                            key={i}
                            gap={12}
                            py={8}
                            style={{
                                alignItems: 'flex-start',
                                borderBottom:
                                    i === breakdown.length - 1
                                        ? 'none'
                                        : `1px solid ${palette.graphite.gray}`,
                            }}
                        >
                            <FlexDiv
                                column
                                gap={2}
                                style={{ flex: 1, minWidth: 0 }}
                            >
                                <TextDefault
                                    bold
                                    color={palette.graphite.heavyMetal}
                                    style={{ display: 'block' }}
                                >
                                    {b.factor}
                                </TextDefault>
                                <TextTiny
                                    color={palette.graphite.charcoal}
                                    style={{ display: 'block' }}
                                >
                                    {b.explanation}
                                </TextTiny>
                            </FlexDiv>
                            <Tag
                                text={`+${b.weight}`}
                                color={palette.vibrant.gold}
                                backgroundColor={palette.pastel.yellow}
                            />
                        </FlexDiv>
                    ))}
                </FlexDiv>
            )}

            {profile.summary && (
                <FlexDiv
                    column
                    gap={8}
                    padding={20}
                    borderRadius={12}
                    mb={24}
                    backgroundColor={palette.pastel.red}
                    style={{
                        border: `1px solid ${palette.pastel.red}`,
                    }}
                >
                    <Heading3 displayBlock>
                        What an attacker now knows
                    </Heading3>
                    <TextDefault color={palette.graphite.heavyMetal}>
                        {profile.summary}
                    </TextDefault>
                    {profile.attacker_takeaway && (
                        <TextTiny
                            color={palette.graphite.charcoal}
                            style={{ display: 'block' }}
                        >
                            {profile.attacker_takeaway}
                        </TextTiny>
                    )}
                </FlexDiv>
            )}

            {error && (
                <Alert severity="error" style={{ marginBottom: 16 }}>
                    {error}
                </Alert>
            )}
            {Array.isArray(warnings) && warnings.length > 0 && (
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                    {warnings.map((w, i) => (
                        <div key={i}>{w}</div>
                    ))}
                </Alert>
            )}

            {onGenerateAttacks && (
                <FlexDiv mt={8}>
                    <ThemedButton
                        mode="primary"
                        text={
                            loadingAttacks
                                ? 'Generating attacks…'
                                : 'Next: review your personalised attack emails →'
                        }
                        $saving={!!loadingAttacks}
                        onClick={onGenerateAttacks}
                        disabled={!!loadingAttacks}
                    />
                </FlexDiv>
            )}
        </div>
    );
}
